import { useMemo, useRef } from "react";
import { Box, Checkbox } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import moment from "moment";
import { generateColumnsDefaultConfiguration } from "../../../components/dataGrid/columnHelpers";
import { formatCurrency } from "../../../utils/mathUtils";
import { PopoverModal } from "../../../components/popoverModal/PopoverModal";
import { CheckboxSeleccion } from "../../../components/checkboxSeleccion/CheckboxSeleccion";

export const useGetColumns = ({
    allowedWrite,
    handleClickEdit,
    handleView,
    handleDeleteAjuste,
    idProfesional,
    seleccionadosProfesional,   // Set — solo se usa vía ref, no como dependencia
    toggleSeleccion,
    seleccionarTodos,
    liquidacionesElegibles,    // Array de liquidaciones que pueden ser seleccionadas
    liqProfAprobadoFinal // Boolean para saber si la liquidación profesional está aprobada desde liquidacion final
}) => {
    // Ref para leer selección actual sin que las columnas dependan de ella
    const seleccionRef = useRef(seleccionadosProfesional);
    seleccionRef.current = seleccionadosProfesional;

    // Calcular si todos están seleccionados e indeterminate state
    const { todosSeleccionados, algunoSeleccionado } = useMemo(() => {
        if (!liquidacionesElegibles || liquidacionesElegibles.length === 0) {
            return { todosSeleccionados: false, algunoSeleccionado: false };
        }
        const ids = liquidacionesElegibles.map(l => l.idLiquidacionProfesionalDetalle);
        return {
            todosSeleccionados: ids.every(id => seleccionadosProfesional.has(id)),
            algunoSeleccionado: ids.some(id => seleccionadosProfesional.has(id))
        };
    }, [liquidacionesElegibles, seleccionadosProfesional]);

    return useMemo(() => {
        const baseColumns = [
            {
                headerName: '',
                field: 'action',
                cellClassName: 'grid-actions-column',
                minWidth: 35,
                maxWidth: 35,
                flex: 35,
                sortable: false,
                renderCell: ({ row }) => (
                    <PopoverModal>
                        <div className='popover-modal-content'>
                            <div
                                aria-hidden='true'
                                onClick={() => handleView(row)}
                                className='item-option clickable-option'
                            >
                                <VisibilityIcon className='edit-icon' />
                                <span className='toggle-action'>Ver detalle de liquidación</span>
                            </div>

                            {allowedWrite && !liqProfAprobadoFinal && (
                                <div
                                    aria-hidden='true'
                                    onClick={() => handleClickEdit(row)}
                                    className='item-option clickable-option'
                                >
                                    <EditIcon className='edit-icon' />
                                    <span className='toggle-action'>Ajuste</span>
                                </div>
                            )}

                            {allowedWrite && !liqProfAprobadoFinal && row.montoAjuste != null && row.montoAjuste !== 0 && (
                                <div
                                    aria-hidden='true'
                                    onClick={() => handleDeleteAjuste(row)}
                                    className='item-option clickable-option'
                                >
                                    <DeleteIcon className='edit-icon' />
                                    <span className='toggle-action'>Eliminar ajuste</span>
                                </div>
                            )}
                        </div>
                    </PopoverModal>
                )
            },
            { field: 'idEvaluacion', headerName: 'Contrato', flex: 80 },
            { field: 'paciente', headerName: 'Paciente', flex: 200 },
            {
                field: 'periodoContrato',
                headerName: 'Periodo contrato',
                flex: 100,
                valueGetter: (_, row) =>
                    `${moment(row.fechaDesdeEvaluacion).format('DD/MM/YYYY')} - ${moment(row.fechaHastaEvaluacion).format('DD/MM/YYYY')}`
            },
            { field: 'fechaDesdeProfesional', headerName: 'F. desde', flex: 100, type: 'date' },
            { field: 'fechaHastaProfesional', headerName: 'F. Hasta', flex: 100, type: 'date' },
            { field: 'diasServicio', headerName: 'Días', flex: 50 },
            { field: 'diasSuspendido', headerName: 'Días susp.', description: 'Días de suspensión', flex: 70 },
            { field: 'totalDias', headerName: 'T. Días', description: 'Total días', flex: 70 },
            { field: 'programa', headerName: 'Programa', flex: 70 },
            { field: 'planCuidado', headerName: 'Plan', flex: 170 },
            { field: 'figuraProfesional', headerName: 'Figura', flex: 90 },
            {
                field: 'montoAjuste',
                headerName: 'Ajuste',
                flex: 60,
                align: 'center',
                headerAlign: 'center',
                renderCell: ({ row }) => {
                    const tieneAjuste = row.montoAjuste != null && row.montoAjuste !== 0;
                    if (!tieneAjuste) return null;
                    return (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <Box sx={{ width: 12, height: 12, background: '#52BA50', borderRadius: '50%' }} />
                        </Box>
                    );
                }
            },
            {
                field: 'tieneConexion',
                headerName: 'Cnx',
                flex: 50,
                valueGetter: (value) => value ? 'SI' : 'NO'
            },
            {
                field: 'liquidacionTotal',
                headerName: 'Total',
                flex: 90,
                valueGetter: (value) => formatCurrency(value || 0)
            },
        ];

        if (allowedWrite) {
            baseColumns.unshift({
                field: 'seleccion',
                headerName: '',
                minWidth: 38,
                maxWidth: 38,
                flex: 38,
                sortable: false,
                noPaddingHeader: true,
                noPadding: true,
                align: 'center',
                headerAlign: 'center',
                renderHeader: () => (
                    <Checkbox
                        size="small"
                        onChange={() => seleccionarTodos(idProfesional, liquidacionesElegibles)}
                        checked={todosSeleccionados}
                        indeterminate={!todosSeleccionados && algunoSeleccionado}
                        disabled={liqProfAprobadoFinal}
                    />
                ),
                renderCell: ({ row }) => (
                    <CheckboxSeleccion
                        idProfesional={idProfesional}
                        idLiquidacion={row.idLiquidacionProfesionalDetalle}
                        // Lee la ref en lugar del estado directamente —
                        // el checkbox igual se actualiza porque CheckboxSeleccion
                        // recibe checked como prop desde la row re-renderizada por MUI DataGrid
                        checked={seleccionRef.current.has(row.idLiquidacionProfesionalDetalle)}
                        disabled={(!row.tieneConexion && row.figuraProfesional.toUpperCase() !== 'EVALUADOR') || liqProfAprobadoFinal}
                        onToggle={toggleSeleccion}
                    />
                )
            });
        }

        return generateColumnsDefaultConfiguration(baseColumns);
        // seleccionadosProfesional NO está en las dependencias — se lee via ref
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedWrite, handleClickEdit, handleView, handleDeleteAjuste, idProfesional, toggleSeleccion, todosSeleccionados, algunoSeleccionado, liquidacionesElegibles]);
};
